import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import EmptyState from "@/components/base/EmptyState";
import ApiState from "@/components/base/ApiState";
import BusinessGrid from "@/components/feature/BusinessGrid";
import CategoryCard from "@/components/feature/CategoryCard";
import { getBusinesses, getCategories, getLocationBySlug } from "@/services/publicApi";
import { useApiResource } from "@/hooks/useApiResource";
import { taluksForDistrict } from "@/config/regions";

export default function LocationDetail() {
  const { slug = "" } = useParams();
  const { data, loading, error, reload } = useApiResource(
    () => Promise.all([getLocationBySlug(slug), getCategories(), getBusinesses()]),
    [slug],
  );
  const location = data?.[0] ?? null;
  const categories = data?.[1] ?? [];
  const businesses = data?.[2] ?? [];

  const taluks = useMemo(() => taluksForDistrict(location?.district), [location]);

  const [categoryFilter, setCategoryFilter] = useState("");
  const [sort, setSort] = useState("relevance");

  useEffect(() => {
    setCategoryFilter("");
    setSort("relevance");
  }, [slug]);

  const inLocation = useMemo(() => businesses.filter((b) => b.locationId === location?.id), [businesses, location]);

  const locationCategories = useMemo(() => {
    const ids = new Set(inLocation.map((b) => b.categoryId));
    return categories.filter((c) => ids.has(c.id));
  }, [inLocation, categories]);

  const filtered = useMemo(() => {
    let list = categoryFilter ? inLocation.filter((b) => b.categoryId === categoryFilter) : inLocation;
    if (sort === "rating") list = [...list].sort((a, b) => b.rating - a.rating);
    else if (sort === "reviews") list = [...list].sort((a, b) => b.reviewCount - a.reviewCount);
    else if (sort === "name") list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    else list = [...list].sort((a, b) => Number(b.isFeatured) - Number(a.isFeatured) || b.rating - a.rating);
    return list;
  }, [inLocation, categoryFilter, sort]);

  const topCategories = useMemo(() => {
    const counts = new Map<string, number>();
    inLocation.forEach((b) => counts.set(b.categoryId, (counts.get(b.categoryId) || 0) + 1));
    return categories
      .filter((c) => counts.has(c.id))
      .sort((a, b) => (counts.get(b.id) || 0) - (counts.get(a.id) || 0))
      .slice(0, 8);
  }, [inLocation, categories]);

  const mapSrc = location
    ? `https://www.google.com/maps?q=${encodeURIComponent(`${location.name}, Karnataka, India`)}&output=embed`
    : "";

  if (!loading && !error && !location) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20">
        <EmptyState
          icon="ri-map-pin-2-line"
          title="Location not found"
          description="This district page doesn't exist or has been renamed."
          action={{ label: "All locations", to: "/locations" }}
        />
      </div>
    );
  }

  return (
    <>
      <Seo
        title={location ? `Businesses in ${location.name}` : "Location"}
        description={location ? `Discover verified local businesses in ${location.name}, ${location.district} district — ${location.description}` : undefined}
        keywords={location ? `businesses in ${location.name}, ${location.name} directory, local services ${location.name}` : undefined}
        path={`/location/${slug}`}
        image={location?.image}
      />

      <div className="relative w-full">
        <div className="absolute inset-0">
          {location && (
            <img src={location.image} alt={`${location.name}`} title={`${location.name} businesses`} className="h-full w-full object-cover object-top" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/75 via-foreground-950/60 to-foreground-950/85"></div>
        </div>
        <div className="relative z-10 mx-auto max-w-[1400px] px-4 md:px-6 py-10 md:py-16">
          <div className="[&_a]:text-background-200 [&_span]:text-background-200">
            <Breadcrumbs items={[{ label: "Locations", to: "/locations" }, { label: location?.name || "…" }]} />
          </div>
          <h1 className="mt-4 font-heading text-3xl md:text-5xl font-extrabold tracking-tight text-background-50">
            {location?.name || "Loading…"}
          </h1>
          <p className="mt-3 max-w-2xl text-sm md:text-base text-background-200 leading-relaxed">
            {location?.description}
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1.5 text-xs font-bold text-foreground-950">
              <i className="ri-store-3-line"></i> {location?.businessCount.toLocaleString()} listings
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/15 px-3 py-1.5 text-xs font-semibold text-background-50 backdrop-blur ring-1 ring-inset ring-background-50/20">
              <i className="ri-map-pin-2-line"></i> {location?.district} district
            </span>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-10 md:py-14">
        {taluks.length > 0 && (
          <section className="mb-12">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="font-heading text-lg md:text-xl font-extrabold text-foreground-950">
                  Taluks in {location?.district} district
                </h2>
                <p className="mt-1 text-sm text-foreground-600">
                  Pick a taluk to see the local businesses around it.
                </p>
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
                <i className="ri-map-2-line"></i> {taluks.length} {taluks.length === 1 ? "taluk" : "taluks"}
              </span>
            </div>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {taluks.map((t) => (
                <Link
                  key={t.slug}
                  to={`/directory?district=${slug}&town=${t.slug}`}
                  className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 hover:bg-primary-50 cursor-pointer"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-community-line"></i>
                  </span>
                  <span className="min-w-0">
                    <span className="block font-heading text-sm font-bold text-foreground-950 leading-snug">{t.name}</span>
                    <span className="mt-0.5 inline-flex items-center gap-1 text-xs font-semibold text-secondary-700">
                      View listings <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5"></i>
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {topCategories.length > 0 && (
          <section className="mb-12">
            <h2 className="font-heading text-lg md:text-xl font-extrabold text-foreground-950">
              Popular categories in {location?.name}
            </h2>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {topCategories.map((c) => (
                <CategoryCard key={c.id} category={c} placeSlug={location?.slug} />
              ))}
            </div>
          </section>
        )}

        <section>
          <div className="flex items-center justify-between gap-4">
            <h2 className="font-heading text-lg md:text-xl font-extrabold text-foreground-950">
              Businesses in {location?.name}
            </h2>
            <Link
              to={`/directory?district=${slug}`}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:text-primary-800 whitespace-nowrap cursor-pointer"
            >
              Open in directory <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-background-200 bg-background-50 p-4">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              aria-label="Filter by category"
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none cursor-pointer"
            >
              <option value="">All categories</option>
              {locationCategories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <div className="ml-auto flex items-center gap-2">
              <label htmlFor="loc-sort" className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
                Sort
              </label>
              <select
                id="loc-sort"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none cursor-pointer"
              >
                <option value="relevance">Best match</option>
                <option value="rating">Highest rated</option>
                <option value="reviews">Most reviewed</option>
                <option value="name">Name (A–Z)</option>
              </select>
            </div>
            <span className="w-full text-xs font-semibold text-foreground-500 sm:w-auto sm:ml-2">
              {loading ? "" : `${filtered.length} listing${filtered.length === 1 ? "" : "s"}`}
            </span>
          </div>

          <div className="mt-5">
            <ApiState
              loading={loading}
              error={error}
              onRetry={reload}
              skeleton={
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-80 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
                  ))}
                </div>
              }
              isEmpty={filtered.length === 0}
              empty={
                <EmptyState
                  icon="ri-store-3-line"
                  title={`No listings yet in ${location?.name}`}
                  description="We're bringing more local businesses on board here. List yours to be the first."
                  action={{ label: "List your business", to: "/list-your-business" }}
                />
              }
            >
              <BusinessGrid businesses={filtered} />
            </ApiState>
          </div>
        </section>

        <section className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          <div className="rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-6">
            <h2 className="font-heading text-base font-bold text-foreground-950">Getting around {location?.name}</h2>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
              {location?.name} is part of {location?.district} district in Karnataka. Whether you're a resident or
              visiting, use the directory to find shops, services, clinics and stay options close by.
            </p>
            <ul className="mt-4 space-y-2.5 text-sm text-foreground-700">
              <li className="flex items-center gap-2.5">
                <i className="ri-checkbox-circle-line text-accent-600"></i> Locality-level business listings
              </li>
              <li className="flex items-center gap-2.5">
                <i className="ri-checkbox-circle-line text-accent-600"></i> Verified owners with real contact details
              </li>
              <li className="flex items-center gap-2.5">
                <i className="ri-checkbox-circle-line text-accent-600"></i> Ratings and reviews from locals
              </li>
            </ul>
          </div>
          <div className="overflow-hidden rounded-lg border border-background-200">
            {mapSrc && (
              <iframe
                title={`Map of ${location?.name}, Karnataka`}
                src={mapSrc}
                className="h-72 w-full lg:h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            )}
          </div>
        </section>
      </div>
    </>
  );
}